import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Archive, MapPin, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { PROPERTY_STATUSES, EC_DECISIONS, RISK_KEYS, UW_GROUPS, gbp, pct, ukDate, decisionBadge, propStatusBadge, riskColor } from "./crmPropertyShared";
import { CrmPropertyMatches } from "./CrmPropertyMatches";

export default function CrmPropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/crm/properties/${id}`); setP(data); setReason(data.decision_reason || ""); }
    catch (e) { if (e?.response?.status === 404) setNotFound(true); else toast.error("Could not load property"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (deal_status) => {
    try { await api.post(`/crm/properties/${id}/status`, { deal_status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setDecision = async (ec_decision) => {
    try { await api.post(`/crm/properties/${id}/decision`, { ec_decision, decision_reason: reason }); toast.success("Decision saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const archive = async () => { if (!window.confirm("Archive this property?")) return; await api.post(`/crm/properties/${id}/archive`); toast.success("Property archived"); navigate("/crm/properties"); };
  const addActivity = async (type) => { const note = window.prompt(`${type} note (optional)`) ?? ""; await api.post(`/crm/properties/${id}/activity`, { type, note }); load(); };
  const downloadDealPack = async () => {
    try {
      const res = await api.get(`/crm/properties/${id}/dealpack`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `DealPack-${p.deal_id}-${(p.postcode || "NA").replace(/\s/g, "")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Deal pack downloaded");
    } catch (e) { toast.error("Could not generate deal pack"); }
  };

  if (notFound) return (
    <CrmShell title="Property">
      <button onClick={() => navigate("/crm/properties")} className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to properties</button>
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-8 text-center text-emerald-100/60" data-testid="prop-not-found">This property could not be found. It may have been archived.</div>
    </CrmShell>
  );
  if (!p) return <CrmShell title="Property"><div className="text-emerald-100/50 text-sm py-8">Loading…</div></CrmShell>;

  const c = p.calc || {}; const u = p.underwriting || {}; const s = c.scenarios || {};
  const summary = [
    ["Purchase", gbp(c.proposed_purchase_price)], ["Total project cost", gbp(c.total_project_cost)],
    ["Target GDV", gbp(c.target_gdv)], ["Est. profit", gbp(c.estimated_profit)],
    ["ROI", pct(c.roi)], ["Gross yield", pct(c.gross_rental_yield)],
  ];

  return (
    <CrmShell title="Property">
      <button onClick={() => navigate("/crm/properties")} className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to properties</button>

      {/* Identity + status + decision */}
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><span className="text-xs font-mono text-brass">{p.deal_id}</span><span className={`text-[11px] px-2 py-0.5 rounded-full ${propStatusBadge(p.deal_status)}`} data-testid="prop-detail-status">{p.deal_status}</span></div>
            <h1 className="text-2xl font-serif text-white mt-1" data-testid="prop-detail-address">{p.address}</h1>
            <div className="text-sm text-emerald-100/60 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{[p.town, p.postcode, p.area].filter(Boolean).join(", ") || "—"}{p.property_type ? ` · ${p.property_type}` : ""}{p.bedrooms ? ` · ${p.bedrooms} bed` : ""}{p.tenure ? ` · ${p.tenure}` : ""}</div>
          </div>
          <div className="text-right">
            {p.ec_decision ? <span className={`text-sm px-3 py-1 rounded-full font-bold ${decisionBadge(p.ec_decision)}`} data-testid="prop-detail-decision">{p.ec_decision}</span> : <span className="text-xs text-emerald-100/40">No decision yet</span>}
            {p.decision_reason && <div className="text-xs text-emerald-100/50 mt-1 max-w-xs">{p.decision_reason}</div>}
            {p.decision_date && <div className="text-[11px] text-emerald-100/40 mt-0.5">Decided {ukDate(p.decision_date)}</div>}
          </div>
        </div>
        {/* Compact summary bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4" data-testid="prop-summary">
          {summary.map(([label, value]) => (
            <div key={label} className="bg-[#091E16] rounded-lg border border-white/5 px-3 py-2">
              <div className="text-[10px] text-emerald-100/50 uppercase tracking-wide">{label}</div>
              <div className="text-white font-mono text-sm mt-0.5">{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* GDV scenarios */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">GDV scenarios</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="prop-scenarios">
              {[["Low", s.low], ["Target", s.target], ["High", s.high]].map(([lbl, sc]) => (
                <div key={lbl} className="bg-[#091E16] rounded-lg border border-white/5 p-3">
                  <div className="text-xs text-emerald-100/50">{lbl}</div>
                  <div className="text-white font-mono">{gbp(sc?.gdv)}</div>
                  <div className="mt-2 text-xs text-emerald-100/60">Profit <span className={`font-medium ${sc?.profit != null && sc.profit < 0 ? "text-red-300" : "text-emerald-300"}`}>{gbp(sc?.profit)}</span></div>
                  <div className="text-xs text-emerald-100/60">ROI <span className="text-white font-medium">{pct(sc?.roi)}</span></div>
                </div>
              ))}
            </div>
          </div>

          {/* Financial inputs */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Underwriting inputs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {UW_GROUPS.map((grp) => (
                <div key={grp.title}>
                  <div className="text-xs font-semibold text-brass mb-1.5">{grp.title}</div>
                  <div className="space-y-1">
                    {grp.fields.map(([k, lbl]) => (
                      <div key={k} className="flex items-center justify-between text-sm"><span className="text-emerald-100/50">{lbl}</span><span className="text-white font-mono">{gbp(u[k])}</span></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-emerald-100/50 text-xs">Total acquisition</div><div className="text-white font-mono">{gbp(c.total_acquisition)}</div></div>
              <div><div className="text-emerald-100/50 text-xs">Total refurb</div><div className="text-white font-mono">{gbp(c.total_refurb)}</div></div>
              <div><div className="text-emerald-100/50 text-xs">Total project cost</div><div className="text-white font-mono">{gbp(c.total_project_cost)}</div></div>
            </div>
          </div>

          {/* Risks */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Risk assessment</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="prop-risks">
              {RISK_KEYS.map(([k, lbl]) => <div key={k} className="flex items-center justify-between bg-[#091E16] rounded-lg px-2.5 py-1.5"><span className="text-emerald-100/50 text-xs">{lbl}</span><span className={`text-xs font-medium ${riskColor(p.risks?.[k])}`}>{p.risks?.[k] || "Not Assessed"}</span></div>)}
            </div>
            {p.risk_notes && <p className="text-xs text-emerald-100/60 mt-3">{p.risk_notes}</p>}
          </div>

          {/* Source */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-2">Source</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              <div><div className="text-emerald-100/50 text-xs">Source type</div><div className="text-white">{p.source_type || "—"}</div></div>
              <div><div className="text-emerald-100/50 text-xs">Source contact</div><div className="text-white">{p.source_contact || "—"}</div></div>
              <div><div className="text-emerald-100/50 text-xs">Asking price</div><div className="text-white">{gbp(p.asking_price)}</div></div>
            </div>
          </div>

          {/* Distribution / matching (Phase 4) */}
          <CrmPropertyMatches propertyId={id} />

          {/* Activity */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="prop-activity">
            <h3 className="text-sm font-semibold text-white mb-3">Activity</h3>
            <div className="flex flex-wrap gap-2 mb-3">{["Note", "Viewing", "Offer", "Negotiation", "Underwriting update"].map((t) => <button key={t} onClick={() => addActivity(t)} data-testid={`prop-add-${t.replace(/\s/g, "-").toLowerCase()}`} className="text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-1.5">+ {t}</button>)}</div>
            <ol className="space-y-2.5">{(p.activities || []).map((a) => <li key={a.id} className="flex gap-3 text-sm"><div className="w-2 h-2 rounded-full bg-brass mt-1.5 shrink-0" /><div><div className="text-white">{a.type}{a.note ? ` — ${a.note}` : ""}</div><div className="text-[11px] text-emerald-100/40">{new Date(a.created_at).toLocaleString("en-GB")} · {a.actor}</div></div></li>)}</ol>
          </div>
        </div>

        {/* Quick actions */}
        <div className="space-y-5">
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white">Quick actions</h3>
            <div><label className="text-xs text-emerald-100/50">Pipeline status</label>
              <select value={p.deal_status} onChange={(e) => changeStatus(e.target.value)} data-testid="prop-detail-status-select" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{PROPERTY_STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
            </div>
            <div><label className="text-xs text-emerald-100/50">Emerald Crown decision</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Decision reason (optional)" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-emerald-100/40" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {EC_DECISIONS.map((dv) => <button key={dv} onClick={() => setDecision(dv)} data-testid={`prop-decide-${dv}`} className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${p.ec_decision === dv ? decisionBadge(dv) : "bg-white/5 text-white hover:bg-white/10"}`}>{dv}</button>)}
                <button onClick={() => setDecision("")} className="text-xs text-emerald-100/50 hover:text-white px-2 py-1.5">Clear</button>
              </div>
            </div>
            <button onClick={() => navigate(`/crm/properties/${id}/edit`)} data-testid="prop-edit" className="w-full text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg py-2">Edit property</button>
            <button onClick={downloadDealPack} data-testid="prop-dealpack" className="w-full flex items-center justify-center gap-1.5 text-sm bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-lg py-2"><FileDown className="w-4 h-4" /> Download deal pack</button>
            <button onClick={archive} data-testid="prop-archive" className="w-full flex items-center justify-center gap-1.5 text-sm bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded-lg py-2"><Archive className="w-4 h-4" /> Archive</button>
          </div>
        </div>
      </div>
    </CrmShell>
  );
}
